import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Users,
  FileText,
  Code,
  Shield,
  TrendingUp,
} from "lucide-react";

interface MythItem {
  title: string;
  explanation: string;
  useCases: string[];
  statistics: string;
  source?: {
    name: string;
    url: string;
  };
  icon: React.ReactNode;
  cta: string;
}

export default function AIMythsAccordion() {
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  const myths: MythItem[] = [
    {
      icon: <Code className="h-8 w-8 text-gray-900" />,
      title: "AI is too complex for employees to learn",
      explanation:
        "Many businesses believe implementing AI requires advanced technical expertise beyond their team's capabilities. However, modern AI tools are increasingly user-friendly and designed to be accessible without deep technical knowledge.",
      useCases: [
        "Email Marketing Optimization: Small businesses can utilize AI-driven platforms to analyze customer engagement and optimize email campaigns, improving open and click-through rates.",
        "Social Media Management: AI tools can schedule posts, analyze audience interactions, and suggest content, simplifying social media strategies for small teams.",
      ],
      statistics:
        "Iowa's AI Adoption: A recent article highlights that 77% of small business owners plan to adopt emerging technologies, including AI and the metaverse.",
      source: {
        name: "Iowa ABLE Savings Plan",
        url: "chrome-extension://efaidnbmnnnibpcajpcglclefindmkaj/https://www.iowaabi.org/upl/downloads/business-record-iowa/67b35f672bf91722c44b1.pdf",
      },
      cta: "Discover how accessible AI tools can transform your workflow.",
    },
    {
      icon: <DollarSign className="h-8 w-8 text-gray-900" />,
      title: "AI adoption requires a massive upfront investment",
      explanation:
        "There's a misconception that AI implementation is prohibitively expensive. In reality, many affordable or even free AI tools are available, making AI accessible to businesses with limited budgets.",
      useCases: [
        "Content Creation: AI-powered writing assistants help small businesses efficiently generate marketing copy, blog posts, and product descriptions.",
        "Inventory Management: AI solutions predict stock needs, reducing overstock and shortages, which is particularly beneficial for small retailers.",
      ],
      statistics:
        "According to Salesforce, 75% of small and medium-sized businesses are at least experimenting with AI, with growing businesses leading in adoption at 83%.",
      source: {
        name: "salesforce.com",
        url: "https://www.salesforce.com/news/stories/smbs-ai-trends-2025/",
      },
      cta: "Start with cost-effective AI solutions that deliver immediate ROI.",
    },
    {
      icon: <Users className="h-8 w-8 text-gray-900" />,
      title: "AI will replace jobs rather than enhance them",
      explanation:
        "The fear that AI will lead to job losses is common. However, AI often automates repetitive tasks, allowing employees to focus on more strategic activities, thereby enhancing job roles.",
      useCases: [
        "Customer Support: AI chatbots handle routine inquiries, freeing human agents to tackle more complex issues.",
        "Data Entry Automation: AI tools can process and input data, reducing manual entry errors and allowing staff to perform higher-value tasks.",
      ],
      statistics:
        "A survey by the U.S. Chamber of Commerce found that 91% of small businesses actively using AI believe it will help them grow in the future.",
      source: {
        name: "uschamber.com",
        url: "https://www.uschamber.com/technology/artificial-intelligence/the-impact-of-technology-on-u-s-small-business",
      },
      cta: "Empower your team with AI tools that enhance their capabilities.",
    },
    {
      icon: <AlertTriangle className="h-8 w-8 text-gray-900" />,
      title: "We don't need AI—our current processes work fine",
      explanation:
        "Some businesses feel their existing workflows are sufficient and do not need AI. However, AI can uncover inefficiencies and provide insights that traditional methods might overlook, leading to improved performance.",
      useCases: [
        "Customer Behavior Analysis: AI analyzes purchasing patterns, helping small retailers tailor their offerings to customer preferences.",
        "Predictive Maintenance: AI predicts equipment failures for manufacturing SMEs, reducing downtime and maintenance costs.",
      ],
      statistics:
        "A study revealed that 29.5% of small and medium business owners believe AI will significantly impact their business in the next several years.",
      source: {
        name: "colorshistle.com",
        url: "https://colorwhistle.com/artificial-intelligence-statistics-for-small-business/",
      },
      cta: "Discover hidden efficiencies and insights with AI-powered analysis.",
    },
    {
      icon: <FileText className="h-8 w-8 text-gray-900" />,
      title: "AI is too difficult to implement",
      explanation:
        "Some organizations are deterred by the perceived complexity of AI implementation. However, technological advancements have led to platforms that simplify the process, making AI more accessible.",
      useCases: [
        "Automated Scheduling: AI tools can manage appointments and bookings, reducing administrative workload.",
        "Expense Tracking: AI-powered applications categorize and monitor expenses, simplifying financial management for small businesses.",
      ],
      statistics:
        "According to a Salesforce report, approximately nine in 10 small and medium-sized business teams use AI, with 60% currently using generative AI or planning to use it.",
      source: {
        name: "salesforce.com",
        url: "https://www.salesforce.com/blog/ai-tools-for-small-business/",
      },
      cta: "Implement AI solutions with our step-by-step guidance and support.",
    },
    {
      icon: <Shield className="h-8 w-8 text-gray-900" />,
      title: "AI is not secure and could expose sensitive data",
      explanation:
        "Data security and privacy concerns are significant barriers to AI adoption. While these concerns are valid, advancements in AI ethics, compliance, and secure deployment options have been developed to mitigate risks.",
      useCases: [
        "Fraud Detection: AI monitors real-time transactions, identifying anomalies that may indicate fraudulent activity.",
        "Network Security: AI systems detect and respond to cyber threats, enhancing the security posture of small businesses.",
      ],
      statistics:
        "A U.S. Chamber of Commerce and Teneo study revealed that 98% of small businesses use AI-enabled tools, with 40% utilizing generative AI tools like chatbots and image creation.",
      source: {
        name: "apnews.com",
        url: "https://apnews.com/article/small-business-artificial-intelligence-productivity-f6fa7b2a1ce0a9f2e5b8b48670b3098a",
      },
      cta: "Implement AI with proper security measures and compliance standards.",
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-gray-900" />,
      title: "AI is just a trend—it won't last",
      explanation:
        "Some view AI as a fad, but its integration across various sectors indicates otherwise. AI continues to evolve, offering solutions to complex problems and driving innovation.",
      useCases: [
        "Personalized Marketing: AI analyzes customer data to create targeted marketing campaigns, increasing engagement and sales.",
        "Recruitment: AI assists in screening resumes and identifying suitable candidates, streamlining the hiring process for small businesses.",
      ],
      statistics:
        "The global AI market is projected to grow from $58.3 billion in 2021 to $309.6 billion by 2026, underscoring AI's enduring impact.",
      source: {
        name: "globenewswire.com",
        url: "https://www.globenewswire.com/news-release/2022/04/07/2418605/0/en/Artificial-Intelligence-AI-Market-worth-309-6-billion-by-2026-Exclusive-Report-by-MarketsandMarkets.html/",
      },
      cta: "Stay ahead of the competition with future-proof AI strategies.",
    },
  ];

  return (
    <div className="space-y-4">
      {myths.map((myth, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-lg overflow-hidden bg-white transition-all duration-300 hover:shadow-md"
        >
          <button
            onClick={() =>
              setExpandedItem(expandedItem === index ? null : index)
            }
            className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="flex items-center space-x-4">
              <div className="bg-gray-100 p-3 rounded-lg">{myth.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900">
                {myth.title}
              </h3>
            </div>
            {expandedItem === index ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          <div
            className={`px-6 overflow-hidden transition-all duration-300 ${
              expandedItem === index ? "max-h-[1000px] py-4" : "max-h-0"
            }`}
          >
            <p className="text-gray-600 mb-4">{myth.explanation}</p>

            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">Use Cases:</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                {myth.useCases.map((useCase, i) => (
                  <li key={i}>{useCase}</li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-medium text-gray-900 mb-2">Statistics:</h4>
              <p className="text-gray-600 mb-2">{myth.statistics}</p>
              {myth.source && (
                <a
                  href={myth.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 transition-colors duration-200"
                >
                  {myth.source.name}
                </a>
              )}
            </div>

            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-[#F98B3D] mt-1 mr-2 flex-shrink-0" />
              <p className="text-[#F98B3D]">{myth.cta}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
